// src/components/ClientPortalDocuments.js

import React, { useState, useCallback } from 'react';

function ClientPortalDocuments({ currentUser, clients, documents, cases, authAxios, showFormMessage }) {
  // Find the specific client profile linked to the current user
  const clientProfile = clients.find(client => client.user === currentUser.id);

  // Filter documents relevant to this specific client profile or their cases
  const clientDocuments = documents.filter(d =>
    d.client === (clientProfile ? clientProfile.id : null) ||
    (d.case && cases.some(c => c.id === d.case && c.client === (clientProfile ? clientProfile.id : null)))
  );

  const [showDocumentVersionsModal, setShowDocumentVersionsModal] = useState(false);
  const [selectedDocumentForVersions, setSelectedDocumentForVersions] = useState(null);
  const [documentVersions, setDocumentVersions] = useState([]);

  // Function to fetch versions for a specific document
  const fetchDocumentVersions = useCallback(async (documentId) => {
    try {
      const response = await authAxios.get(`documents/${documentId}/versions/`);
      setDocumentVersions(response.data);
    } catch (err) {
      showFormMessage('danger', 'Failed to fetch document versions.');
      console.error("Error fetching document versions:", err);
    }
  }, [authAxios, showFormMessage]);

  const openDocumentVersionsModal = useCallback((document) => {
    setSelectedDocumentForVersions(document);
    fetchDocumentVersions(document.id);
    setShowDocumentVersionsModal(true);
  }, [fetchDocumentVersions]);

  const closeDocumentVersionsModal = useCallback(() => {
    setSelectedDocumentForVersions(null);
    setShowDocumentVersionsModal(false);
    setDocumentVersions([]);
  }, []);

  return (
    <div>
      <h2 className="text-2xl font-bold text-primary mb-1">Your Documents</h2>
      {clientProfile ? (
        <p className="text-gray-500 mb-6">Access documents related to your profile and cases, {clientProfile.first_name} {clientProfile.last_name}.</p>
      ) : (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <p className="text-yellow-700">Your user account is not yet linked to a client profile. Please contact the firm administrator to view your documents.</p>
        </div>
      )}

      {clientDocuments.length > 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Associated Case</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Uploaded At</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(clientDocuments || []).map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-800">{doc.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{doc.category}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{doc.case_title || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm flex gap-2 flex-wrap">
                      <a href={doc.file} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-success text-white text-xs font-medium rounded-full hover:opacity-90">View</a>
                      <button className="px-3 py-1 bg-secondary text-white text-xs font-medium rounded-full hover:opacity-90" onClick={() => openDocumentVersionsModal(doc)}>Versions</button>
                      {doc.share_link && (
                        <a href={doc.share_link} target="_blank" rel="noopener noreferrer" className="px-3 py-1 border border-accent text-accent text-xs font-medium rounded-full hover:bg-accent hover:text-white transition-colors">Share Link</a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-700">No documents found associated with your client profile or cases.</p>
        </div>
      )}

      {/* Document Versions Modal */}
      {showDocumentVersionsModal && selectedDocumentForVersions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="bg-primary text-white px-6 py-4 rounded-t-lg flex items-center justify-between shrink-0">
              <h5 className="text-lg font-semibold">Versions for: {selectedDocumentForVersions.title}</h5>
              <button onClick={closeDocumentVersionsModal} className="text-white/70 hover:text-white text-xl leading-none">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <h6 className="text-gray-600 font-semibold mb-3">Version History</h6>
              {(documentVersions || []).length > 0 ? (
                <ul className="space-y-2">
                  {(documentVersions || []).map(version => (
                    <li key={version.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                      <div>
                        <strong>v{version.version_number}</strong> - Uploaded {new Date(version.uploaded_at).toLocaleString()} by {version.uploaded_by_username}
                        {version.notes && <small className="block text-gray-400 mt-1">Notes: {version.notes}</small>}
                      </div>
                      <a href={version.file} target="_blank" rel="noopener noreferrer" className="px-3 py-1 border border-accent text-accent text-xs font-medium rounded-full hover:bg-accent hover:text-white transition-colors">View</a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400">No previous versions available.</p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 text-right shrink-0">
              <button className="px-4 py-2 bg-secondary text-white text-sm font-medium rounded-lg hover:opacity-90" onClick={closeDocumentVersionsModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientPortalDocuments;
